import React, { PureComponent, Fragment } from 'react'
import i18n from 'i18next'
import AnimateHeight from 'react-animate-height'
import { reaction } from 'mobx'
import * as R from 'ramda'
import { observer } from 'mobx-react'

import ReportPageTitle from '../ReportPageTitle'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'
import AiOverview from './AiOverview'

@observer
class UrlVariable extends PureComponent {
    constructor(props) {
        super(props)
        const { question } = this.props
        const optionReduce = question.optionSet.options.reduce((obj, o) => {
            obj.isOpen[o.id.toUpperCase()] = false
            obj.responses[o.id.toUpperCase()] = {
                numberOfResponses: 0,
                responses: [],
            }
            return obj
        }, {
            isOpen: {},
            responses: {},
        })
        this.state = {
            isLoading: false,
            isOpen: optionReduce.isOpen,
            responses: optionReduce.responses,
            numberOfResponses: optionReduce.numberOfResponses,
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.getReportData()
            },
        )
    }

    componentDidMount() {
        this.getReportData()
    }

    mapResponses = (response) => (
        R.sortWith([
            R.descend(R.prop('numberOfResponses')),
            R.ascend(R.prop('label')),
        ], response.labels.map((label, index) => ({
            label,
            numberOfResponses: R.pathOr(0, ['numberOfResponses', index], response),
            surveyDataIds: R.pathOr([], ['surveyDataIds', index], response),
        })))
    )

    getReportData = async () => {
        const { question, reportStore } = this.props
        const { isOpen } = this.state
        const { questionTypeCode } = question
        const { appStore } = reportStore
        try {
            const request = JSON.stringify({
                questionId: question.id,
                questionType: questionTypeCode,
                questionOptionId: null,
                filter: {
                    state: reportStore.getReportStates(),
                    surveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'include'),
                    excludeSurveyDataIds: reportStore.getSurveyDataIdFilter(reportStore.filters.openEnded, 'exclude'),
                    ...R.omit(['openEnded'], reportStore.filters),
                },
            })

            const response = await appStore.http.post(`/report/v1/${reportStore.surveyId}/textquestion`, request)

            const responses = Object.entries(R.or(response, {})).reduce((map, [optionId, optionData]) => (
                Object.assign(map, {
                    [optionId.toUpperCase()]: {
                        numberOfResponses: optionData.numberOfResponses.reduce((a, b) => a + b),
                        responses: this.mapResponses(optionData),
                    },
                })
            ), {})

            reportStore.openEnded.set(question.id, responses)

            this.setState({
                responses,
                isLoading: false,
                isOpen,
            })
        } catch (e) {
            console.error(`Error getting getReportData -> ${e}`)
        }
    }

    renderFilter = (label, optionId, surveyDataIds) => {
        const { reportStore, question } = this.props
        const { showQuickFilter, filterByOpenEnded, quickFilters } = reportStore
        const filterKey = `${question.id}_${optionId}_${label}`
        const hasFilter = quickFilters.has(filterKey)
        const className = hasFilter
            ? 'fa-regular fa-square-check'
            : 'fa-regular fa-square'

        if (!showQuickFilter) return null

        return (
            <div className="quick-filter-check-container">
                <i
                    onClick={() => filterByOpenEnded(question, label, surveyDataIds, optionId)}
                    className={className}
                    style={{ marginRight: '5px' }}
                />
            </div>
        )
    }

    resultRows = () => {
        const { responses, isOpen } = this.state
        const { question, reportStore, store } = this.props
        const { survey } = store

        return (
            <div style={{ marginTop: '10px' }}>
                {question.optionSet.options.map((option, i) => (
                    <Fragment key={option.id}>
                        <div
                            className=""
                            data-index={i}
                            style={{
                                display: 'flex',
                                fontSize: '1em',
                                justifyContent: 'space-between',
                                marginTop: '30px',
                                padding: '0px 25px',
                            }}
                        >
                            <div style={{ fontWeight: 700 }}>
                                {
                                    getOptionTitle({
                                        langs: option.langs,
                                        locale: reportStore.locale,
                                        defaultLocale: survey.metadata.defaultLocale,
                                        initialLocale: question.metadata.initialLocale,
                                    })
                                }
                                <span style={{ marginLeft: '5px' }}>
                                    (
                                    {i18n.t('report_total_responses')}
                                    :
                                    {R.pathOr(0, [option.id.toUpperCase(), 'numberOfResponses'], responses)}
                                    )
                                </span>
                            </div>
                            <a
                                data-id={option.id.toUpperCase()}
                                onClick={this.toggleOpen}
                                style={{
                                    cursor: 'pointer',
                                }}
                            >
                                Toggle
                            </a>
                        </div>
                        <AnimateHeight
                            duration={250}
                            height={isOpen[option.id.toUpperCase()] ? 'auto' : 0}
                            style={{
                                padding: '0px 25px',
                            }}
                        >
                            {Object.hasOwnProperty.call(responses, option.id.toUpperCase()) && (
                                responses[option.id.toUpperCase()].responses
                                    .map(({ label, numberOfResponses, surveyDataIds }, index) => (
                                        <div
                                            className="textResponse"
                                            data-index={index}
                                        >
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                {this.renderFilter(label, option.id, surveyDataIds)}
                                                {label}
                                            </div>
                                            <span>
                                                (
                                                {numberOfResponses}
                                                )
                                            </span>
                                        </div>
                                    ))
                            )}
                        </AnimateHeight>
                        {!isOpen[option.id.toUpperCase()] && (
                            <div style={{ paddingBottom: '5px', borderBottom: '1px solid #DDD' }} />
                        )}
                    </Fragment>
                ))}
            </div>
        )
    }

    toggleOpen = (e) => {
        const { isOpen } = this.state
        const id = e.currentTarget.attributes.getNamedItem('data-id').value

        this.setState({
            isOpen: {
                ...isOpen,
                [id]: !isOpen[id],
            },
        })
    }

    render() {
        const {
            store,
            reportStore,
            question,
            pageId,
        } = this.props
        const { isLoading } = this.state
        const { survey } = store
        const questionTitle = getQuestionTitle({
            content: question.content.shortTitle,
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        })
        return (
            <Panel cssClass="question textQuestion">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={questionTitle}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                <div id={`questionData_${question.pageQuestionId}`}>
                    {isLoading && <div className="loader" />}
                    {this.resultRows()}
                </div>
            </Panel>
        )
    }
}

export default UrlVariable
